import { FOCUS_RING_NO_RADIUS, Icon } from '@amelio/ui-library';

import type { ReactNode } from 'react';

const CHIP_CLASS_NAME =
    'border-obj-line flex items-center gap-1.5 rounded-full border bg-white py-0.5 pr-2 text-[12px]';

/** An avatar fills its box edge to edge where a glyph leaves air around itself. */
const LEADING_INSET = { avatar: 'pl-0.5', icon: 'pl-2' } as const;

interface ObjectiveParticipantChipProps {
    leadingInset: keyof typeof LEADING_INSET;
    leading: ReactNode;
    name: string;
    /** What follows the name, such as how many people a value brings in. */
    trailing?: ReactNode;
    /** Absent when the chip cannot be removed: the locked owner, or a value the reader may not pick. */
    removal?: { label: string; onRemove: () => void; dataTestId: string };
    dataTestId: string;
}

/** One pick standing in the field — a person or a company value — named and, when it may go, removable. */
export default function ObjectiveParticipantChip({
    leadingInset,
    leading,
    name,
    trailing,
    removal,
    dataTestId,
}: ObjectiveParticipantChipProps) {
    return (
        <span
            className={`${CHIP_CLASS_NAME} ${LEADING_INSET[leadingInset]}`}
            data-testid={dataTestId}
        >
            {leading}

            <span className={'text-navy text-[14px] font-medium'}>{name}</span>

            {trailing}

            {removal && (
                <button
                    type={'button'}
                    onClick={removal.onRemove}
                    aria-label={removal.label}
                    className={`rounded-full p-1 ${FOCUS_RING_NO_RADIUS}`}
                    data-testid={removal.dataTestId}
                >
                    <Icon
                        name={'essential-close'}
                        size={'sm'}
                        color={'inherit'}
                    />
                </button>
            )}
        </span>
    );
}
